import styled from "styled-components";
import React, { useState } from "react";
import PanelHeader from "../../components/molecules/PanelHeader";
import { nvrTools, uploadTypes } from "../../lib/types";
import { stageNames } from "../../lib/workflow";
import colours from "../../lib/colours";
import RemovalSummary from "../Other/RemovalSummary";
import { formatNumberToFixed } from "../../utils/otherUtils";
import OffsetRequirements from "../Other/OffsetRequirements";
import { Label, Value, ValueNumber } from "../../components/atoms/Text";
import FilledButton from "../../components/atoms/FilledButton";
import Dialog from "../../components/organisms/Dialog";
import Final from "./Final";
import ProgressMessage from "../../components/atoms/ProgressMessage";
import OffsetSummary from "../Other/OffsetSummary";
import HabitatGainDetails from "../Other/HabitatGainDetails";
import { useUploadTypeContext } from "../../context/ContextProvider";
import { reportEmptyDetailValues } from "../../lib/constants"

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const BodyContainer = styled.div`
  overflow: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  letter-spacing: -0.01875rem;
`;

const ContentContainer = styled.div`
  overflow: auto;
  flex: 1;
  padding-block: 8px;
`;

const SubHeading = styled.h4`
  margin-top: 16px;
  margin-bottom: 10px;
  padding-inline: 12px;
  font-weight: 600;
  color: ${colours.th1};
`;

const MessageContainer = styled.div`
  padding: 12px;
  padding-top: 24px;
`;

const ErrorSpan = styled.span`
  font-weight: 500;
  color: ${colours.errord1};
`;

const LocationDetailsContainer = styled.div`
  padding-inline: 12px;
  color: ${colours.darkerGrey};
  font-size: 14px;
  margin-bottom: 36px;
`;

export const DetailsItem = styled.div``;

export const DetailsValue = styled(Value)`
  text-transform: capitalize;
`;

const AdditionalAddresses = styled.div`
  font-style: italic;
  color: ${colours.th1l2};
`;

const FooterButton = styled(FilledButton)`
  transition: background-color 0.3s linear;
  height: 56px;
  border-radius: 0;
`;

export const pathwayStyles: Record<string, any> = {
  Basic: {
    color: colours.basicPathwayText,
    backgroundColor: colours.basicPathway,
  },
  Intermediate: {
    color: colours.intermediatePathwayText,
    backgroundColor: colours.intermediatePathway,
  },
  Detailed: {
    color: colours.detailedPathwayText,
    backgroundColor: colours.detailedPathway,
  },
};

export const locationStyles: Record<number, any> = {
  1: { color: colours.location1Text, backgroundColor: colours.location1 },
  2: { color: colours.location2Text, backgroundColor: colours.location2 },
  3: { color: colours.location3Text, backgroundColor: colours.location3 },
};

interface IProps {
  mode: nvrTools;
  close: any;
  setStage: any;
  locationResults?: any;
  removalResults?: any;
  offsetResults?: any;
  fetching?: boolean;
  requestError?: boolean;
  pastRemovalNumber?: number;
}

function SummaryReport({
  mode,
  close,
  locationResults,
  removalResults,
  offsetResults,
  fetching,
  requestError,
  pastRemovalNumber,
}: IProps) {
  const { uploadType } = useUploadTypeContext();
  const [dialogOpen, setDialogOpen] = useState(false);

  const renderLocationDetails = () => {
    const details = locationResults?.results;
    const lgasArray = details.localGovernmentAreas.value;
    const lgas = lgasArray.length > 0
      ? lgasArray.map((x: string) => x.toLowerCase()).join(", ")
      : reportEmptyDetailValues.lgas;
    const aboriginalPartiesArray = details.registeredAboriginalParties.value;
    const aboriginalParties = aboriginalPartiesArray.length > 0
      ? aboriginalPartiesArray.join(", ")
      : reportEmptyDetailValues.raps;
    const addresses = details.addresses.value;
    const coordinates = details.featuresCentroid.value
      .map((x: string) => formatNumberToFixed(Number(x), 5))
      .join(", ");
    return (
      <>
        <SubHeading>Location details</SubHeading>
        <LocationDetailsContainer>
          <DetailsItem>
            <Label>Local Government Areas</Label>
            <DetailsValue>{lgas}</DetailsValue>
          </DetailsItem>
          <DetailsItem>
            <Label>Registered Aboriginal Parties</Label>
            <DetailsValue>{aboriginalParties}</DetailsValue>
          </DetailsItem>
          <DetailsItem>
            <Label>Addresses</Label>
            {addresses.length === 0 ? (
              <DetailsValue>{reportEmptyDetailValues.addresses}</DetailsValue>
            ) : (
            <>
            {addresses.slice(0, 5).map((x: string, index: number) => (
              <DetailsValue key={index} style={{ display: "block" }}>
                {x.toLowerCase()}
              </DetailsValue>
            ))}
            {addresses.length > 5 && (
              <AdditionalAddresses>
                ({addresses.length - 5} additional addresses not listed)
              </AdditionalAddresses>
            )}
            </>
          )}
          </DetailsItem>
          <DetailsItem>
            <Label>Coordinates</Label>
            <ValueNumber>{coordinates}</ValueNumber>
          </DetailsItem>
        </LocationDetailsContainer>
      </>
    );
  };

  const renderRemovalContent = () => {
    const pathway: string = removalResults.results.assessmentPathway.value;
    return (
      <>
        <SubHeading>Summary of native vegetation to be removed</SubHeading>
        <RemovalSummary removalResults={removalResults?.results} />
        {pathway !== "Detailed" && (
          <>
            <SubHeading>Offset requirements if approval is granted</SubHeading>
            <OffsetRequirements
              locationResults={locationResults?.results}
              removalResults={removalResults?.results}
            />
          </>
        )}
      </>
    );
  };

  const renderOffsetContent = () => {
    return (
      <>
        <SubHeading>Summary of offset site</SubHeading>
        <OffsetSummary offsetResults={offsetResults?.results} />
        {uploadType === uploadTypes.simple && (
          <>
            <SubHeading>
              Habitat units of gain for the proposed offset site
            </SubHeading>
            <HabitatGainDetails
              locationResults={locationResults?.results}
              offsetResults={offsetResults?.results}
            />
          </>
        )}
      </>
    );
  };

  const renderContent = () => {
    if (fetching || (locationResults === undefined && !requestError)) {
      return (
        <MessageContainer>
          <ProgressMessage message="Creating summary" />
        </MessageContainer>
      );
    }
    if (requestError) {
      return (
        <MessageContainer>
          <ErrorSpan>Unable to fetch summary data from server</ErrorSpan>
        </MessageContainer>
      );
    }
    return (
      <>
        <ContentContainer>
          {renderLocationDetails()}
          {mode === nvrTools.removal
            ? renderRemovalContent()
            : renderOffsetContent()}
        </ContentContainer>
        <FooterButton onClick={() => setDialogOpen(true)}>Next</FooterButton>
      </>
    );
  };

  return (
    <Container>
      <PanelHeader title={stageNames["summaryReport"]} onClose={close} />
      <BodyContainer>{renderContent()}</BodyContainer>
      <Dialog open={dialogOpen} showHeader={false} showFooter={false}>
        <Final
          mode={mode}
          removalResults={removalResults}
          onClose={() => setDialogOpen(false)}
          pastRemovalNumber={pastRemovalNumber}
        />
      </Dialog>
    </Container>
  );
}

export default SummaryReport;
